import { z } from "zod";
import { isValidPostalCode } from "@/lib/validations/postalCode.validation";
import { isValidTva } from "@/lib/validations/tva.validation";
import { isValidSiret } from "@/lib/validations/siret.validation";
import { isValidEori } from "@/lib/validations/eori.validation";
import { isValidSelectorString } from "@/lib/validations/selector.validation";
import { i18nMessage } from "@/lib/i18nMessage";

// Base object schema without refinement (for extending)
const baseBillingEntityObjectSchema = z.object({
  companyName: z
    .string({ required_error: i18nMessage("shared:forms.billingEntity.companyNameRequired") })
    .min(1, i18nMessage("shared:forms.billingEntity.companyNameRequired")),
  street: z
    .string({ required_error: i18nMessage("shared:forms.billingEntity.streetRequired") })
    .min(1, i18nMessage("shared:forms.billingEntity.streetRequired")),
  postalCode: z
    .string({ required_error: i18nMessage("shared:forms.billingEntity.postalCodeRequired") })
    .min(1, i18nMessage("shared:forms.billingEntity.postalCodeRequired"))
    .refine((postalCode) => isValidPostalCode(postalCode), {
      message: i18nMessage("validation:invalidPostalCode"),
    }),
  city: z
    .string({ required_error: i18nMessage("shared:forms.billingEntity.cityRequired") })
    .min(1, i18nMessage("shared:forms.billingEntity.cityRequired")),
  country: z
    .string({ required_error: i18nMessage("shared:forms.billingEntity.countryRequired") })
    .min(1, i18nMessage("shared:forms.billingEntity.countryRequired")),
  numberTVA: z.string().optional(),
  companySiret: z
    .string({ required_error: i18nMessage("shared:forms.billingEntity.siretRequired") })
    .min(1, i18nMessage("shared:forms.billingEntity.siretRequired"))
    .refine((siret) => isValidSiret(siret), {
      message: i18nMessage("shared:forms.billingEntity.siretInvalid"),
    }),
  noEori: z.boolean().optional(),
  companyEori: z.string().optional(),
  email: z
    .string({ required_error: i18nMessage("shared:forms.billingEntity.emailRequired") })
    .email(i18nMessage("validation:invalidEmail")),
  emailBis: z.union([z.literal(""), z.string().email(i18nMessage("validation:invalidEmail"))]),
});

// TVA validation refinement function (required only for France)
const tvaRefinement = (data: { country: string; numberTVA?: string }, ctx: z.RefinementCtx) => {
  if (data.country === "FR") {
    if (!data.numberTVA || data.numberTVA.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("shared:forms.billingEntity.vatRequired"),
        path: ["numberTVA"],
      });
    } else if (!isValidTva(data.numberTVA)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("shared:forms.billingEntity.vatInvalid"),
        path: ["numberTVA"],
      });
    }
  }
};

// EORI validation refinement function
const eoriRefinement = (data: { noEori?: boolean; companyEori?: string }, ctx: z.RefinementCtx) => {
  if (!data.noEori) {
    if (!data.companyEori || data.companyEori.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("shared:forms.billingEntity.eoriRequired"),
        path: ["companyEori"],
      });
    } else if (!isValidEori(data.companyEori)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18nMessage("shared:forms.billingEntity.eoriInvalid"),
        path: ["companyEori"],
      });
    }
  }
};

// Combined refinement for both TVA and EORI
const billingEntityRefinement = (
  data: { country: string; numberTVA?: string; noEori?: boolean; companyEori?: string },
  ctx: z.RefinementCtx,
) => {
  tvaRefinement(data, ctx);
  eoriRefinement(data, ctx);
};

// Base schema with TVA and EORI refinement
export const baseBillingEntitySchema =
  baseBillingEntityObjectSchema.superRefine(billingEntityRefinement);

export type BaseBillingEntityFormData = z.infer<typeof baseBillingEntitySchema>;

export const profileBillingEntitySchema = baseBillingEntityObjectSchema
  .extend({
    isDefault: z.boolean(),
  })
  .superRefine(billingEntityRefinement);

export type ProfileBillingEntityFormData = z.infer<typeof profileBillingEntitySchema>;

// Register flow billing entity schema (paymentMethod not filled during RegisterStepCompany)
export const registerFlowBillingEntitySchema = baseBillingEntityObjectSchema
  .extend({
    billingRequestId: z.string().optional(),
    paymentMethod: isValidSelectorString(
      i18nMessage("shared:forms.billingEntity.paymentMethodRequired"),
    ).optional(),
  })
  .superRefine(billingEntityRefinement);

export const DEFAULT_BILLING_ENTITY_VALUES: BaseBillingEntityFormData = {
  companyName: "",
  street: "",
  postalCode: "",
  city: "",
  country: "FR",
  numberTVA: "",
  companySiret: "",
  noEori: false,
  companyEori: "",
  email: "",
  emailBis: "",
};

export const DEFAULT_PROFILE_BILLING_ENTITY_VALUES: ProfileBillingEntityFormData = {
  ...DEFAULT_BILLING_ENTITY_VALUES,
  isDefault: false,
};
